import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { API_URL } from '../config';

export default function CAVerification({ isDarkMode, onClose, companies, currentUserId }) {
  console.log('🔍 CAVerification mount - companies:', companies?.length || 0, companies);
  
  const [activeTab, setActiveTab] = useState('export');
  const [exportData, setExportData] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const [caMappings, setCaMappings] = useState({});
  const [csvData, setCsvData] = useState([]); // CSV data ze souboru
  const [previewData, setPreviewData] = useState([]); // Náhled změn pro import
  const [showPreview, setShowPreview] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('');
  
  // Nové state pro mapování sloupců
  const [fileHeaders, setFileHeaders] = useState([]);
  const [showColumnMapping, setShowColumnMapping] = useState(false);
  const [columnMapping, setColumnMapping] = useState({
    ico: '',
    ca: '',
    prm: ''
  });
  
  // State pro načtené společnosti
  const [loadedCompanies, setLoadedCompanies] = useState([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(true);

  // Funkce pro načítání pouze relevantních společností podle IČO z CSV
  const loadRelevantCompanies = async (processedData) => {
    if (!processedData || processedData.length === 0) {
      console.log('⚠️ Žádná data k načítání společností');
      setLoadingMessage('⚠️ Žádná data k načítání společností');
      return [];
    }

    try {
      setIsLoadingCompanies(true);
      console.log('🔄 Načítám relevantní společnosti z API...');
      
      // Extrahuj všechna IČO z importovaných dat
      const icoList = processedData.map(item => item.ico).filter(ico => ico);
      console.log(`📋 Hledám ${icoList.length} unikátních IČO v databázi`);
      setLoadingMessage(`📋 Hledám ${icoList.length} IČO v databázi...`);
      
      // Načti společnosti po batch-ích (100 IČO najednou)
      let allCompanies = [];
      const batchSize = 100;
      const totalBatches = Math.ceil(icoList.length / batchSize);
      
      for (let i = 0; i < icoList.length; i += batchSize) {
        const batch = icoList.slice(i, i + batchSize);
        const currentBatch = Math.floor(i/batchSize) + 1;
        
        console.log(`📄 Načítám batch ${currentBatch}/${totalBatches} (${batch.length} IČO)`);
        setLoadingMessage(`📄 Načítám batch ${currentBatch}/${totalBatches} (${batch.length} IČO)`);
        
        // Debug info pro batch
        console.log(`🔍 Batch ${currentBatch} IČO vzorka:`, batch.slice(0, 3));
        
        // Aktualizace progress baru pro tento batch
        const batchProgress = Math.round((currentBatch / totalBatches) * 30) + 40; // 40-70% range
        setLoadingProgress(batchProgress);
        
        try {
          const token = localStorage.getItem('token');
          if (currentBatch === 1) {
            console.log(`🔐 Token pro API: ${token ? 'Existuje' : 'Chybí'}`);
            console.log(`🌐 API URL: ${API_URL}/companies/by-ico`);
            console.log(`📦 Posílám batch data:`, { icos: batch });
          }
          
          const response = await fetch(`${API_URL}/companies/by-ico`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': token ? `Bearer ${token}` : ''
            },
            body: JSON.stringify({ icos: batch })
          });
          
          if (currentBatch === 1) {
            console.log(`📡 První batch response:`, {
              status: response.status,
              statusText: response.statusText,
              ok: response.ok,
              headers: Object.fromEntries(response.headers.entries())
            });
          }
          
          if (!response.ok) {
            const errorText = await response.text();
            console.warn(`⚠️ Batch ${currentBatch} selhal: ${response.status} - ${errorText}`);
            setLoadingMessage(`⚠️ Batch ${currentBatch} selhal (${response.status}), pokračujem...`);
            
            // Pro první batch, zobrazíme podrobnou chybu
            if (currentBatch === 1) {
              console.error(`❌ Kritická chyba v prvním batchi:`, {
                status: response.status,
                statusText: response.statusText,
                error: errorText,
                url: `${API_URL}/companies/by-ico`
              });
              setLoadingMessage(`❌ Kritická chyba: ${response.status} ${errorText}`);
              throw new Error(`API chyba: ${response.status} - ${errorText}`);
            }
            
            await new Promise(resolve => setTimeout(resolve, 1000));
            continue;
          }
          
          const batchData = await response.json();
          let batchCompanies = [];
          
          if (Array.isArray(batchData)) {
            batchCompanies = batchData;
          } else if (batchData && Array.isArray(batchData.companies)) {
            batchCompanies = batchData.companies;
          } else if (batchData && Array.isArray(batchData.data)) {
            batchCompanies = batchData.data;
          } else {
            console.warn(`⚠️ Batch ${currentBatch}: Neočekávaný formát odpovědi`, batchData);
          }
          
          allCompanies = [...allCompanies, ...batchCompanies];
          console.log(`📊 Batch ${currentBatch}: +${batchCompanies.length} společností (celkem: ${allCompanies.length})`);
          setLoadingMessage(`📊 Batch ${currentBatch}/${totalBatches}: ${allCompanies.length} společností načteno`);
          
          // Aktualizace UI s aktuálními daty
          setLoadedCompanies([...allCompanies]);
          
        } catch (batchError) {
          console.error(`❌ Chyba v batch ${currentBatch}:`, batchError);
          setLoadingMessage(`❌ Chyba v batch ${currentBatch}: ${batchError.message}`);
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
      
      console.log(`✅ Načteno celkem ${allCompanies.length} relevantních společností z ${icoList.length} IČO`);
      setLoadingMessage(`✅ Načteno ${allCompanies.length} relevantních společností!`);
      setLoadedCompanies(allCompanies);
      setIsLoadingCompanies(false);
      return allCompanies;
      
    } catch (error) {
      console.error('❌ Chyba při načítání relevantních společností:', error);
      setLoadingMessage(`❌ Chyba: ${error.message}`);
      // Fallback na předané companies prop
      setLoadedCompanies(companies || []);
      setIsLoadingCompanies(false);
      return companies || [];
    }
  };

  // Funkce pro načítání všech společností z API s paginací (fallback)
  const loadAllCompanies = async () => {
    try {
      setIsLoadingCompanies(true);
      console.log('🔄 Načítám všechny společnosti z API...');
      
      let allCompanies = [];
      let currentPage = 1;
      let hasMorePages = true;
      const limit = 100; // Větší batch pro rychlejší načítání
      
      while (hasMorePages) {
        console.log(`📄 Načítám stránku ${currentPage}...`);
        
        const response = await fetch(`${API_URL}/companies?limit=${limit}&page=${currentPage}&userId=${currentUserId || localStorage.getItem('lastUserId') || ''}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const pageData = await response.json();
        console.log(`🔍 Stránka ${currentPage}:`, typeof pageData, Array.isArray(pageData));
        
        // Extrakce společností ze stránky
        let pageCompanies = [];
        if (Array.isArray(pageData)) {
          pageCompanies = pageData;
        } else if (pageData && Array.isArray(pageData.companies)) {
          pageCompanies = pageData.companies;
        } else if (pageData && Array.isArray(pageData.data)) {
          pageCompanies = pageData.data;
        } else {
          console.warn('⚠️ Neočekávaný formát odpovědi z API na stránce', currentPage, ':', pageData);
          break;
        }
        
        allCompanies = [...allCompanies, ...pageCompanies];
        console.log(`📊 Celkem načteno: ${allCompanies.length} společností`);
        
        // Kontrola, jestli jsou další stránky
        if (pageCompanies.length < limit) {
          hasMorePages = false;
        } else {
          currentPage++;
        }
        
        // Aktualizace progress pro UI
        if (allCompanies.length > 0) {
          setLoadedCompanies([...allCompanies]); // Aktualizuj UI průběžně
        }
      }
      
      console.log(`✅ Načteno celkem ${allCompanies.length} společností z API`);
      
      setLoadedCompanies(allCompanies);
      setIsLoadingCompanies(false);
      
    } catch (error) {
      console.error('❌ Chyba při načítání společností:', error);
      // Fallback na předané companies prop
      setLoadedCompanies(companies || []);
      setIsLoadingCompanies(false);
    }
  };
  
  // useEffect pro načítání společností při mount komponenty - VYPNUTO pro efektivitu
  // Společnosti se načítají až po zpracování Excel souboru pomocí loadRelevantCompanies
  useEffect(() => {
    // Pouze inicializace bez načítání dat
    console.log('📂 CA Verification dialog otevřen - čekám na nahrání Excel souboru');
  }, []);

  // Funkce pro export IČO
  const handleExport = () => {
    const icoNumbers = [];
    
    // Kontrola, že loadedCompanies je pole
    const companies = Array.isArray(loadedCompanies) ? loadedCompanies : [];
    
    companies.forEach(company => {
      if (company.ico && company.ico.trim()) {
        // Normalizace IČO - odstranění všech ne-číselných znaků
        let normalizedIco = company.ico.replace(/[^\d]/g, '');
        
        // Přidání pouze pokud je IČO validní (má délku 6-8 čísel)
        if (normalizedIco.length >= 6 && normalizedIco.length <= 8) {
          icoNumbers.push(normalizedIco);
        }
      }
    });

    // Odstranění duplikátů
    const uniqueIcos = [...new Set(icoNumbers)];
    
    // Vytvoření řetězce oddělovaného čárkami
    const exportText = uniqueIcos.join(',');
    
    setExportData(exportText);
    
    // Automatické stažení do .txt souboru
    const blob = new Blob([exportText], { type: 'text/plain;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `ca_ico_export_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Funkce pro parsování CSV nebo Excel souboru - pouze čte hlavičky
  const parseFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const fileName = file.name.toLowerCase();
          
          if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
            // Excel soubor
            const arrayBuffer = e.target.result;
            const workbook = XLSX.read(arrayBuffer, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
            
            if (jsonData.length === 0) {
              reject(new Error('Excel soubor je prázdný'));
              return;
            }
            
            // Přečtení hlaviček - ponechám původní názvy (bez lowercase)
            const originalHeaders = jsonData[0].map(h => String(h).trim());
            console.log('🔍 Nalezené sloupce v Excel souboru:', originalHeaders);
            
            // Vrátím data spolu s hlavičkami pro další zpracování
            resolve({
              headers: originalHeaders,
              rawData: jsonData,
              type: 'excel'
            });
            
          } else if (fileName.endsWith('.csv')) {
            // CSV soubor
            const text = e.target.result;
            const lines = text.trim().split('\n');
            
            if (lines.length === 0) {
              reject(new Error('CSV soubor je prázdný'));
              return;
            }
            
            // Přečtení hlaviček - ponechám původní názvy
            const originalHeaders = lines[0].split(',').map(h => h.trim());
            console.log('🔍 Nalezené sloupce v CSV souboru:', originalHeaders);
            
            // Vrátím data spolu s hlavičkami pro další zpracování
            resolve({
              headers: originalHeaders,
              rawData: lines,
              type: 'csv'
            });
          } else {
            reject(new Error('Nepodporovaný formát souboru. Použijte .xlsx, .xls nebo .csv'));
            return;
          }
        } catch (error) {
          reject(new Error(`Chyba při parsování souboru: ${error.message}`));
        }
      };
      
      reader.onerror = () => reject(new Error('Chyba při čtení souboru'));
      
      // Čtení podle typu souboru
      const fileName = file.name.toLowerCase();
      if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
        reader.readAsArrayBuffer(file);
      } else {
        reader.readAsText(file, 'utf-8');
      }
    });
  };

  // Funkce pro zpracování dat s uživatelským mapováním
  const processDataWithMapping = (fileData, mapping) => {
    console.log('🔄 processDataWithMapping začíná...');
    console.log('📄 FileData:', fileData);
    console.log('🗺️ Mapping:', mapping);
    
    const processedData = [];
    
    console.log('🔍 Zpracovávám data s mapováním:', { 
      type: fileData.type, 
      headers: fileData.headers, 
      mapping,
      totalRows: fileData.rawData.length 
    });
    
    try {
    if (fileData.type === 'excel') {
      console.log('📊 Zpracovávám Excel soubor...');
      const { rawData } = fileData;
      
      for (let i = 1; i < rawData.length; i++) {
        const row = rawData[i];
        if (!row || row.length === 0) {
          if (i <= 3) console.log(`⏭️ Přeskakuji prázdný řádek ${i}`);
          continue;
        }
        
        // Vytvoření objektu řádku podle indexů sloupců
        const rowData = {};
        fileData.headers.forEach((header, index) => {
          rowData[header] = row[index] ? String(row[index]).trim() : '';
        });
        
        // Debug pro první řádek
        if (i === 1) {
          console.log('📋 První řádek dat:', rowData);
          console.log('🗺️ Mapování pro první řádek:', {
            ico_column: mapping.ico,
            ca_column: mapping.ca,
            prm_column: mapping.prm
          });
        }
        
        // Získání hodnot pomocí uživatelského mapování
        const icoValue = mapping.ico ? rowData[mapping.ico] || '' : '';
        const caValue = mapping.ca ? rowData[mapping.ca] || '' : '';
        const prmValue = mapping.prm ? rowData[mapping.prm] || '' : '';
        
        // Validace IČO
        const normalizedIco = String(icoValue).replace(/[^\d]/g, '');
        if (normalizedIco.length >= 6) {
          processedData.push({
            ico: normalizedIco,
            ca: caValue,
            prm_vodafone: prmValue
          });
          
          // Debug pro první řádky
          if (processedData.length <= 3) {
            console.log(`📋 Zpracován řádek ${processedData.length}: ICO "${icoValue}" → "${normalizedIco}", CA: "${caValue}", PRM: "${prmValue}"`);
          }
        }
      }
    } else if (fileData.type === 'csv') {
      const { rawData } = fileData;
      
      for (let i = 1; i < rawData.length; i++) {
        const values = rawData[i].split(',').map(v => v.trim());
        
        if (values.length !== fileData.headers.length) {
          continue;
        }
        
        const rowData = {};
        fileData.headers.forEach((header, index) => {
          rowData[header] = values[index];
        });
        
        // Získání hodnot pomocí uživatelského mapování
        const icoValue = mapping.ico ? rowData[mapping.ico] || '' : '';
        const caValue = mapping.ca ? rowData[mapping.ca] || '' : '';
        const prmValue = mapping.prm ? rowData[mapping.prm] || '' : '';
        
        // Debug pro první řádek
        if (i === 1) {
          console.log('🔍 Extrahované hodnoty z prvního řádku:', {
            ico: icoValue,
            ca: caValue,
            prm: prmValue
          });
        }
        
        // Validace IČO
        const normalizedIco = String(icoValue).replace(/[^\d]/g, '');
        if (normalizedIco.length >= 6) {
          processedData.push({
            ico: normalizedIco,
            ca: caValue,
            prm_vodafone: prmValue
          });
          
          // Debug pro první validní řádek
          if (processedData.length === 1) {
            console.log('✅ První validní řádek přidán:', processedData[0]);
          }
        } else if (i <= 3) {
          console.log(`❌ Řádek ${i}: Neplatné IČO "${icoValue}" → "${normalizedIco}" (délka: ${normalizedIco.length})`);
        }
      }
    }
    
    console.log('📊 Celkem zpracováno řádků:', processedData.length);
    return processedData;
    
    } catch (error) {
      console.error('❌ Chyba při zpracování dat:', error);
      throw error;
    }
  };

  // Funkce pro potvrzení mapování a zpracování dat
  const confirmColumnMapping = async () => {
    console.log('🔄 confirmColumnMapping zahájeno');
    console.log('🗺️ Column mapping:', columnMapping);
    console.log('📄 CSV data:', csvData);
    console.log('📊 CSV data type:', typeof csvData);
    console.log('📋 File headers:', fileHeaders);
    
    if (!columnMapping.ico || !columnMapping.ca || !columnMapping.prm) {
      console.log('❌ Chybějící mapování:', {
        ico: columnMapping.ico,
        ca: columnMapping.ca, 
        prm: columnMapping.prm
      });
      alert('Vyberte prosím všechny potřebné sloupce');
      return;
    }
    
    if (!csvData || !csvData.rawData || csvData.rawData.length === 0) {
      console.log('❌ Žádná CSV data k zpracování:', csvData);
      alert('Žádná data k zpracování. Nahrajte prosím soubor znovu.');
      return;
    }
    
    console.log(`📊 Celkem řádků v souboru: ${csvData.rawData.length}`);
    console.log(`📋 Typ souboru: ${csvData.type}`);
    
    // Nastavení loading stavu
    setIsProcessing(true);
    setLoadingMessage('Zpracovávám data...');
    setLoadingProgress(25);
    
    try {
      console.log('🔄 Volám processDataWithMapping...');
      // Zpracování dat s uživatelským mapováním
      const processedData = processDataWithMapping(csvData, columnMapping);
      console.log(`✅ processDataWithMapping vrátilo: ${processedData ? processedData.length : 'null/undefined'} řádků`);
      console.log('🔍 Type of processedData:', typeof processedData, Array.isArray(processedData));
      console.log('🔍 První 3 processedData:', processedData ? processedData.slice(0, 3) : 'N/A');
      
      // Kontrola, že processedData je array
      if (!Array.isArray(processedData)) {
        throw new Error('Zpracovaná data nejsou ve formátu pole (array)');
      }
      
      setLoadingProgress(40);
      setLoadingMessage('Načítám relevantní společnosti...');
      
      // Načti pouze společnosti, které jsou v CSV souboru
      const relevantCompanies = await loadRelevantCompanies(processedData);
      console.log(`📊 Načteno ${relevantCompanies.length} relevantních společností z ${processedData.length} CSV řádků`);
      
      setLoadingProgress(70);
      setLoadingMessage('Vytvářím mapování...');
      
      // Vytvoření mapování IČO -> data pro pozdější import
      const mappings = {};
      processedData.forEach(row => {
        mappings[row.ico] = {
          ca: row.ca,
          prm_vodafone: row.prm_vodafone  // Opravený název pole
        };
      });
      
      setCaMappings(mappings);
      
      setLoadingProgress(75);
      setLoadingMessage('Generuji náhled změn...');
      
      // Vytvoření náhledu změn - používám relevantní společnosti místo všech
      createPreview(processedData, relevantCompanies);
      
      setLoadingProgress(100);
      
      // Skrytí mapování UI po malé pauze
      setTimeout(() => {
        setShowColumnMapping(false);
        setIsProcessing(false);
        setLoadingMessage('');
        setLoadingProgress(0);
      }, 500);
      
    } catch (error) {
      console.error('❌ Chyba při zpracování dat:', error);
      alert(`Chyba při zpracování dat: ${error.message}`);
      setIsProcessing(false);
      setLoadingMessage('');
      setLoadingProgress(0);
    }
  };

  // Funkce pro vytvoření náhledu změn z procesovaných dat
  const createPreview = (processedData, relevantCompanies = null) => {
    const preview = [];
    let matchedCompanies = 0;
    
    // Použij relevantní společnosti pokud jsou poskytnuté, jinak fallback na loadedCompanies
    const companies = relevantCompanies && Array.isArray(relevantCompanies) 
      ? relevantCompanies 
      : (Array.isArray(loadedCompanies) ? loadedCompanies : []);
    
    console.log('🔍 CreatePreview: Celkem companies k porovnání:', companies.length);
    console.log('🔍 CreatePreview: Type of companies:', typeof companies, Array.isArray(companies));
    
    if (companies.length > 0) {
      console.log('🔍 CreatePreview: První 3 companies IČO:', companies.slice(0, 3).map(c => c?.ico || 'N/A'));
    }
    
    processedData.forEach((item, index) => {
      if (item.ico) {
        const normalizedIco = item.ico.toString().replace(/[^\d]/g, '');
        
        // Debug pro první řádky
        if (index < 3) {
          console.log(`🔍 Řádek ${index + 1}: ICO "${item.ico}" → normalizované "${normalizedIco}"`);
        }
        
        // Najdi odpovídající společnost v databázi
        const company = companies.find(c => {
          if (!c.ico) return false;
          const companyIco = c.ico.replace(/[^\d]/g, '');
          return companyIco === normalizedIco;
        });
        
        // Debug pro první řádky - ukáže jestli našla shodu
        if (index < 3) {
          console.log(`🔍 Řádek ${index + 1}: Hledám ICO "${normalizedIco}" → ${company ? 'NALEZENO' : 'NENALEZENO'}`);
          if (!company) {
            console.log(`🔍 Řádek ${index + 1}: Žádná shoda pro ICO "${normalizedIco}"`);
          }
        }
        
        if (company) {
          matchedCompanies++;
          const changes = [];
          
          // Kontrola změn CA
          if (item.ca && item.ca !== '' && company.ca !== item.ca) {
            changes.push({
              field: 'CA',
              oldValue: company.ca || 'N/A',
              newValue: item.ca
            });
          }
          
          // Kontrola změn PRM Vodafone
          if (item.prm_vodafone && item.prm_vodafone !== '' && company.prm !== item.prm_vodafone) {
            changes.push({
              field: 'PRM Vodafone',
              oldValue: company.prm || 'N/A',
              newValue: item.prm_vodafone
            });
          }
          
          if (changes.length > 0) {
            preview.push({
              companyName: company.name || 'Bez názvu',
              ico: normalizedIco,
              changes
            });
          }
        }
      }
    });
    
    console.log(`📊 Statistiky: ${matchedCompanies} společností nalezeno, ${preview.length} bude změněno`);
    setPreviewData(preview);
    setShowPreview(true);
  };

  // Funkce pro načtení CSV nebo Excel souboru - nová verze s UI mapováním
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setCsvFile(file);
    
    try {
      const fileData = await parseFile(file);
      setCsvData(fileData);
      setFileHeaders(fileData.headers);
      
      console.log(`📋 Načten soubor s ${fileData.headers.length} sloupci:`, fileData.headers);
      
      // Pokus o automatické mapování (optional)
      const autoMapping = {
        ico: fileData.headers.find(h => 
          h.toLowerCase().includes('ico') || 
          h.toLowerCase().includes('ičo') || 
          h.toLowerCase() === 'ic'
        ) || '',
        ca: fileData.headers.find(h => 
          h.toLowerCase().includes('ca') || 
          h.toLowerCase().includes('channel')
        ) || '',
        prm: fileData.headers.find(h => 
          h.toLowerCase().includes('prm') || 
          h.toLowerCase().includes('vodafone') ||
          h.toLowerCase() === 'prm_vodafone'
        ) || ''
      };
      
      setColumnMapping(autoMapping);
      console.log('🤖 Automatické mapování:', autoMapping);
      
      // Zobrazí UI pro mapování sloupců
      setShowColumnMapping(true);
      setShowPreview(false);
      
    } catch (error) {
      alert(`Chyba při zpracování souboru: ${error.message}`);
    }
  };

  // Funkce pro provedení aktualizace
  const handleUpdate = async () => {
    if (!csvData || csvData.length === 0) {
      alert('Nejprve nahrajte Excel nebo CSV soubor');
      return;
    }
    
    setIsProcessing(true);
    setLoadingProgress(0);
    setLoadingMessage('Začínám aktualizaci...');
    
    // Použij novou bulk update funkci místo starého způsobu
    await performBulkUpdate();
  };

  // Funkce pro bulk aktualizaci
  const performBulkUpdate = async () => {
    try {
      setLoadingProgress(80);
      setLoadingMessage('Připravuji bulk aktualizaci...');
      
      // Připrav data pro bulk aktualizaci
      const updates = [];
      
      Object.entries(caMappings).forEach(([ico, mapping]) => {
        const updateData = { ico };
        
        if (mapping.ca !== undefined && mapping.ca !== null && mapping.ca !== '') {
          updateData.ca = mapping.ca;
        }
        
        if (mapping.prm_vodafone !== undefined && mapping.prm_vodafone !== null && mapping.prm_vodafone !== '') {
          updateData.prm_vodafone = mapping.prm_vodafone;
        }
        
        // Přidej pouze pokud má co aktualizovat
        if (updateData.ca !== undefined || updateData.prm_vodafone !== undefined) {
          updates.push(updateData);
        }
      });
      
      console.log(`📊 Připraveno ${updates.length} aktualizací pro bulk update`);
      
      if (updates.length === 0) {
        throw new Error('Žádná data k aktualizaci');
      }
      
      setLoadingProgress(90);
      setLoadingMessage(`Provádím bulk aktualizaci ${updates.length} společností...`);
      
      // Proveď bulk aktualizaci
      const response = await fetch(`${API_URL}/companies/bulk-ca-update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ updates })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Chyba při bulk aktualizaci: ${errorText}`);
      }
      
      const result = await response.json();
      console.log('✅ Bulk aktualizace výsledek:', result);
      
      setLoadingProgress(100);
      setLoadingMessage(`Bulk aktualizace dokončena! Aktualizováno ${result.updatedCount}/${result.totalRequested} společností.`);
      
      setTimeout(() => {
        setIsProcessing(false);
        setLoadingProgress(0);
        setLoadingMessage('');
        
        let alertMessage = `Bulk aktualizace byla úspěšně dokončena!\n`;
        alertMessage += `Aktualizováno: ${result.updatedCount} z ${result.totalRequested} společností`;
        
        if (result.errors && result.errors.length > 0) {
          alertMessage += `\nChyby: ${result.errors.length}`;
          console.warn('Chyby při aktualizaci:', result.errors);
        }
        
        alert(alertMessage);
        
        // Resetování formuláře
        setCsvFile(null);
        setCsvData([]);
        setCaMappings({});
        setShowColumnMapping(false);
        setColumnMapping({});
        setFileHeaders([]);
        setPreviewData([]);
        setLoadedCompanies([]);
        setShowPreview(false);
        
        // Refresh stránky pro zobrazení aktualizovaných dat
        window.location.reload();
      }, 2000);
      
    } catch (error) {
      console.error('❌ Chyba při zpracování aktualizace:', error);
      setIsProcessing(false);
      setLoadingProgress(0);
      setLoadingMessage('');
      alert(`Chyba při aktualizaci: ${error.message}`);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'} rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto custom-scrollbar`}>
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">🏢 Ověření CA a Vlastníka</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-2xl"
            >
              ×
            </button>
          </div>

          {/* Tabs */}
          <div className="flex mb-6 border-b border-gray-300 dark:border-gray-600">
            <button
              onClick={() => setActiveTab('export')}
              className={`px-4 py-2 font-medium ${
                activeTab === 'export'
                  ? `border-b-2 border-blue-500 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
              }`}
            >
              📤 Export IČO
            </button>
            <button
              onClick={() => setActiveTab('import')}
              className={`px-4 py-2 font-medium ml-4 ${
                activeTab === 'import'
                  ? `border-b-2 border-blue-500 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
              }`}
            >
              📥 Import CA a PRM
            </button>
          </div>

          {/* Export Tab */}
          {activeTab === 'export' && (
            <div>
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-3">Export IČO pro ověření</h3>
                <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Exportuje všechna IČO ze systému pro externí ověření CA a PRM Vodafone hodnot.
                </p>
                
                <button
                  onClick={handleExport}
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium"
                >
                  📤 Exportovat IČO
                </button>
                
                {exportData && (
                  <div className="mt-4">
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Exportovaná IČO (automaticky staženo):
                    </label>
                    <textarea
                      value={exportData}
                      readOnly
                      className={`w-full h-32 px-3 py-2 border rounded-lg font-mono text-sm ${
                        isDarkMode
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-gray-50 border-gray-300 text-gray-800'
                      }`}
                      placeholder="Exportovaná IČO se zobrazí zde..."
                    />
                    <p className={`text-sm mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Celkem exportováno: {exportData.split(',').length} IČO
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Import Tab */}
          {activeTab === 'import' && (
            <div>
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-3">Import CA a PRM Vodafone dat</h3>
                <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Nahrajte Excel (.xlsx, .xls) nebo CSV soubor s aktualizovanými CA a PRM Vodafone hodnotami.
                </p>

                {/* Podporované formáty sloupců */}
                <div className={`mb-4 p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <h4 className="font-medium mb-2">📋 Podporované názvy sloupců:</h4>
                  <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mb-3`}>
                    <p><strong>IČO:</strong> ico, ičo, ic, icocompany</p>
                    <p><strong>CA:</strong> ca, channel, agent</p>
                    <p><strong>PRM:</strong> prm, vodafone, prm_vodafone, "prm vodafone", prmvodafone</p>
                  </div>
                  <div>
                    <h5 className="font-medium mb-1">Příklad formátu:</h5>
                    <pre className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
ICO,CA,PRM_VODAFONE{'\n'}12345678,ANO,123456{'\n'}87654321,NE,654321
                    </pre>
                  </div>
                </div>

                {/* Upload */}
                <div className="mb-4">
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileUpload}
                    className={`block w-full text-sm ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-500'
                    } file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium ${
                      isDarkMode
                        ? 'file:bg-gray-600 file:text-white hover:file:bg-gray-500'
                        : 'file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200'
                    }`}
                  />
                </div>

                {/* Column Mapping UI */}
                {showColumnMapping && fileHeaders.length > 0 && (
                  <div className={`mb-6 p-4 border rounded-lg ${isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-gray-50'}`}>
                    <h4 className="font-medium mb-3">🗂️ Mapování sloupců</h4>
                    <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Vyberte, které sloupce z vašeho souboru odpovídají požadovaným polím:
                    </p>
                    {isLoadingCompanies && (
                      <p className={`text-sm mb-4 text-blue-500 font-medium`}>
                        ⏳ Načítám společnosti... (načteno: {Array.isArray(loadedCompanies) ? loadedCompanies.length : 0})
                      </p>
                    )}
                    {!isLoadingCompanies && (
                      <p className={`text-sm mb-4 text-green-600 font-medium`}>
                        ✅ Načteno {Array.isArray(loadedCompanies) ? loadedCompanies.length : 0} společností z databáze
                      </p>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      {/* IČO mapování */}
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>IČO *</span>
                        </label>
                        <select
                          value={columnMapping.ico}
                          onChange={(e) => setColumnMapping(prev => ({ ...prev, ico: e.target.value }))}
                          className={`w-full px-3 py-2 border rounded-md ${isDarkMode 
                            ? 'bg-gray-600 border-gray-500 text-white' 
                            : 'bg-white border-gray-300 text-gray-900'
                          }`}
                        >
                          <option value="">-- Vyberte sloupec --</option>
                          {fileHeaders.map((header, index) => (
                            <option key={index} value={header}>{header}</option>
                          ))}
                        </select>
                      </div>

                      {/* CA mapování */}
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>CA *</span>
                        </label>
                        <select
                          value={columnMapping.ca}
                          onChange={(e) => setColumnMapping(prev => ({ ...prev, ca: e.target.value }))}
                          className={`w-full px-3 py-2 border rounded-md ${isDarkMode 
                            ? 'bg-gray-600 border-gray-500 text-white' 
                            : 'bg-white border-gray-300 text-gray-900'
                          }`}
                        >
                          <option value="">-- Vyberte sloupec --</option>
                          {fileHeaders.map((header, index) => (
                            <option key={index} value={header}>{header}</option>
                          ))}
                        </select>
                      </div>

                      {/* PRM mapování */}
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>PRM Vodafone *</span>
                        </label>
                        <select
                          value={columnMapping.prm}
                          onChange={(e) => setColumnMapping(prev => ({ ...prev, prm: e.target.value }))}
                          className={`w-full px-3 py-2 border rounded-md ${isDarkMode 
                            ? 'bg-gray-600 border-gray-500 text-white' 
                            : 'bg-white border-gray-300 text-gray-900'
                          }`}
                        >
                          <option value="">-- Vyberte sloupec --</option>
                          {fileHeaders.map((header, index) => (
                            <option key={index} value={header}>{header}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={confirmColumnMapping}
                        disabled={!columnMapping.ico || !columnMapping.ca || !columnMapping.prm || isProcessing || isLoadingCompanies}
                        className={`px-4 py-2 rounded-lg font-medium ${
                          !columnMapping.ico || !columnMapping.ca || !columnMapping.prm || isProcessing || isLoadingCompanies
                            ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                            : 'bg-blue-500 hover:bg-blue-600 text-white'
                        }`}
                      >
                        {isLoadingCompanies ? '⏳ Načítám společnosti...' : (isProcessing ? '⏳ Zpracovávám...' : '✅ Potvrdit mapování')}
                      </button>
                      
                      <button
                        onClick={() => {
                          setShowColumnMapping(false);
                          setFileHeaders([]);
                          setCsvFile(null);
                        }}
                        className={`px-4 py-2 rounded-lg border ${isDarkMode 
                          ? 'border-gray-500 text-gray-300 hover:bg-gray-600' 
                          : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        ❌ Zrušit
                      </button>
                    </div>
                  </div>
                )}

                {/* Loading indikátor pro zpracování dat */}
                {isProcessing && (
                  <div className={`mb-6 p-6 border rounded-lg text-center ${isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-gray-50'}`}>
                    <div className="mb-3">
                      <div className={`inline-block w-8 h-8 border-4 border-t-blue-500 border-gray-300 rounded-full animate-spin ${isDarkMode ? 'border-gray-600' : ''}`}></div>
                    </div>
                    <p className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {loadingMessage}
                    </p>
                    <div className={`w-full bg-gray-300 rounded-full h-2 ${isDarkMode ? 'bg-gray-600' : ''}`}>
                      <div 
                        className="bg-blue-500 h-2 rounded-full transition-all duration-300" 
                        style={{ width: `${loadingProgress}%` }}
                      ></div>
                    </div>
                    <p className={`text-sm mt-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {loadingProgress}% dokončeno
                    </p>
                  </div>
                )}

                {/* Preview */}
                {showPreview && previewData.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-medium mb-3">📋 Náhled změn ({previewData.length} společností):</h4>
                    <div className={`max-h-96 overflow-y-auto custom-scrollbar border rounded-lg ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                      <table className="w-full text-sm">
                        <thead className={`sticky top-0 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                          <tr>
                            <th className={`px-4 py-2 text-left font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Společnost</th>
                            <th className={`px-4 py-2 text-left font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>IČO</th>
                            <th className={`px-4 py-2 text-left font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Pole</th>
                            <th className={`px-4 py-2 text-left font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Stará hodnota</th>
                            <th className={`px-4 py-2 text-left font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Nová hodnota</th>
                          </tr>
                        </thead>
                        <tbody>
                          {previewData.map((company, companyIndex) => (
                            company.changes.map((change, changeIndex) => (
                              <tr 
                                key={`${companyIndex}-${changeIndex}`}
                                className={`border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}
                              >
                                {changeIndex === 0 && (
                                  <>
                                    <td rowSpan={company.changes.length} className={`px-4 py-2 border-r ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                                      {company.companyName}
                                    </td>
                                    <td rowSpan={company.changes.length} className={`px-4 py-2 border-r ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                                      {company.ico}
                                    </td>
                                  </>
                                )}
                                <td className={`px-4 py-2 border-r ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                                  {change.field}
                                </td>
                                <td className={`px-4 py-2 border-r ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                                  {change.oldValue}
                                </td>
                                <td className="px-4 py-2 font-medium text-green-600 dark:text-green-400">
                                  {change.newValue}
                                </td>
                              </tr>
                            ))
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="mt-4 flex gap-3">
                      <button
                        onClick={handleUpdate}
                        disabled={isProcessing}
                        className={`px-4 py-2 bg-green-500 hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg font-medium ${
                          isProcessing ? 'opacity-50' : ''
                        }`}
                      >
                        {isProcessing ? '⏳ Aktualizuji...' : '✅ Provést aktualizaci'}
                      </button>
                      
                      <button
                        onClick={() => {
                          setCsvFile(null);
                          setCsvData([]);
                          setCaMappings({});
                          setPreviewData([]);
                          setShowPreview(false);
                        }}
                        disabled={isProcessing}
                        className="px-4 py-2 bg-gray-500 hover:bg-gray-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg font-medium"
                      >
                        🚫 Zrušit
                      </button>
                    </div>
                  </div>
                )}

                {/* Progress Bar */}
                {isProcessing && (
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium">Probíhá aktualizace...</span>
                      <span className="text-sm">{loadingProgress}%</span>
                    </div>
                    <div className={`w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700`}>
                      <div 
                        className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" 
                        style={{ width: `${loadingProgress}%` }}
                      ></div>
                    </div>
                    {loadingMessage && (
                      <p className={`text-sm mt-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {loadingMessage}
                      </p>
                    )}
                  </div>
                )}

                {/* No preview message */}
                {showPreview && previewData.length === 0 && (
                  <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <p className={`text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      ℹ️ Nebyla nalezena žádná data k aktualizaci. 
                      Zkontrolujte, zda soubor obsahuje správné IČO a hodnoty.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
